import { type RefObject, useEffect, useState } from 'react';

type Options = {
  durationMs?: number;
  disabled?: boolean;
};

/**
 * Animate a number from 0 → target when the element enters the viewport.
 */
export function useCountUp<T extends HTMLElement>(
  ref: RefObject<T | null>,
  target: number,
  { durationMs = 1200, disabled = false }: Options = {}
) {
  const [value, setValue] = useState(disabled ? target : 0);

  useEffect(() => {
    if (disabled) {
      setValue(target);
      return;
    }

    const el = ref.current;
    if (!el || target <= 0) {
      setValue(target);
      return;
    }

    let rafId = 0;
    let startTs: number | null = null;
    let io: IntersectionObserver | null = null;

    const tick = (ts: number) => {
      if (startTs == null) startTs = ts;
      const t = Math.min(1, (ts - startTs) / durationMs);
      const eased = 1 - (1 - t) ** 3;
      setValue(Math.round(target * eased));
      if (t < 1) rafId = requestAnimationFrame(tick);
    };

    io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        io?.disconnect();
        setValue(0);
        rafId = requestAnimationFrame(tick);
      },
      { threshold: 0.35 }
    );
    io.observe(el);

    return () => {
      io?.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [ref, target, durationMs, disabled]);

  return value;
}
